import { useState, useEffect, useRef, useCallback } from 'react'
import { claimRootPrompt, releaseRootPrompt } from '../../lib/rootPrompt'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useCommonT } from '../../i18n'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'

// The other half of the reset flow. Clicking a reset link signs the user in
// with a recovery session and drops them back on the site; without this they
// are silently signed in and never asked for the new password they came for.
//
// Self-arming, two ways, and the second one is deliberately paranoid:
//  - the PASSWORD_RECOVERY auth event, the documented signal and the primary
//    authority, and
//  - the initial URL hash, because on a full page load the event can fire
//    before this component ever subscribes.
//
// The hash fallback is the dangerous one. "Anything containing type=recovery"
// would let an attacker mail a signed-in user a link to /#type=recovery and
// phish them into setting a password the attacker chose, against their REAL
// session. So the hash may arm this only when all three hold: the hash parses
// to an exact type=recovery param, it carries an access_token, and that token
// is the token the current session is actually running on. A recovery link
// the user really clicked satisfies all three; a crafted URL satisfies none.
//
// Copy is kept identical to AuthModal's validation strings on purpose (see
// AuthModal.tsx): two different messages for the same mistake reads like two
// different bugs.

// Messages: common:auth.password_mismatch, .password_too_short (shared with
// AuthModal and the account page) and common:auth.recovery.not_armed.
const MIN_PASSWORD_LENGTH = 8

// Read ONCE at mount, in a lazy initializer, because Supabase scrubs the hash
// out of the URL as soon as it has consumed it. Pure: no side effects here.
function readRecoveryHash(): { token: string } | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  // Exact param, never a substring: `#note=type=recovery` parses to a `note`
  // param and is correctly ignored.
  if (params.get('type') !== 'recovery') return null
  // eslint-disable-next-line i18next/no-literal-string -- URL param name
  const token = params.get('access_token')
  if (!token) return null
  return { token }
}

export default function SetPasswordModal() {
  const { updatePassword } = useAuth()
  const t = useCommonT()
  const [hashRecovery] = useState(readRecoveryHash)
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  // Defense in depth: save() re-checks this, so no render path can submit a
  // password change for a modal that was never legitimately armed.
  const armedRef = useRef(false)

  const arm = useCallback(() => {
    armedRef.current = true
    // Outranks every other root prompt, so this always succeeds and evicts
    // whatever was up. Someone who clicked a reset link is here for THIS, and
    // a "set your password" box underneath an announcement is the worst of the
    // three collisions.
    // eslint-disable-next-line i18next/no-literal-string -- root prompt id
    claimRootPrompt('password-recovery', () => {})
    setOpen(true)
  }, [])

  useEffect(() => {
    if (!supabase) return
    const { data: { subscription } } = supabase.auth.onAuthStateChange(event => {
      if (event === 'PASSWORD_RECOVERY') arm()
    })
    return () => subscription.unsubscribe()
  }, [arm])

  // The hash fallback's third condition: the recovery token in the URL has to
  // be the token this session is running on.
  useEffect(() => {
    if (!hashRecovery || !supabase) return
    let cancelled = false
    void supabase.auth.getSession()
      .then(({ data }) => {
        if (cancelled) return
        if (data?.session?.access_token === hashRecovery.token) arm()
      })
      .catch(() => { /* no session, no arming */ })
    return () => { cancelled = true }
  }, [hashRecovery, arm])

  if (!open) return null

  const close = () => {
    armedRef.current = false
    // eslint-disable-next-line i18next/no-literal-string -- root prompt id
    releaseRootPrompt('password-recovery')
    setOpen(false)
    setPassword(''); setConfirm(''); setError(null); setDone(false)
  }

  const save = async () => {
    if (!armedRef.current) { setError(t('auth.recovery.not_armed')); return }
    if (password !== confirm) { setError(t('auth.password_mismatch')); return }
    if (password.length < MIN_PASSWORD_LENGTH) { setError(t('auth.password_too_short', { n: MIN_PASSWORD_LENGTH })); return }
    setBusy(true); setError(null)
    try {
      const { error: err } = await updatePassword(password)
      if (err) setError(err); else setDone(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className={`fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 ${OVERLAY_ABOVE_AD}`}
      onClick={() => { if (!busy) close() }}
    >
      <div
        className={`bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-sm w-full space-y-4 ${PANEL_FITS_OVERLAY}`}
        onClick={e => e.stopPropagation()}
      >
        {done ? (
          <>
            {/* Deliberately NOT "Password updated": that is the body copy
                below, and two elements with the same text make the state
                ambiguous to read and to query. */}
            <h2 className="text-lg font-semibold text-gray-100">{t('auth.recovery.all_set')}</h2>
            <p className="text-sm text-gray-300">{t('auth.recovery.updated')}</p>
            <button
              type="button"
              onClick={close}
              className="w-full px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              {t('auth.done')}
            </button>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-gray-100">{t('auth.recovery.title')}</h2>
            <div className="space-y-1">
              <label htmlFor="recovery-password" className="block text-xs text-gray-400">{t('auth.recovery.new_password')}</label>
              <input
                id="recovery-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg bg-gray-950 border border-gray-700 text-sm text-gray-100 focus:border-gray-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="recovery-confirm" className="block text-xs text-gray-400">{t('auth.confirm_password')}</label>
              <input
                id="recovery-confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg bg-gray-950 border border-gray-700 text-sm text-gray-100 focus:border-gray-500 focus:outline-none disabled:opacity-50"
              />
            </div>
            {error && (
              <p className="text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded p-2">
                {error}
              </p>
            )}
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy}
              className="w-full px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {t('auth.recovery.save')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
